import { autoinject, transient } from "aurelia-dependency-injection";
import { bindable } from "aurelia-templating";
import * as moment from "moment";
import { DataType } from "../../../../../../../models/queries/queries.models";
import { NotificationClientService, NotificationType } from "../../../../../../../services/basics/notification-client.service";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { type IFilterableVesselPortList, ParamFilterModel, PortRiskProfileFilterModel } from "../../../models/port-risk-profile.models";
import { PortsTerminalBerthsResponseItem, PortTerminalAndBerthResponse } from "../../../models/ports-and-terminals.models";
import { PortsAndTerminalsService } from "../../../services/ports-and-terminals.service";

@autoinject
@transient()
export class PortRiskProfileFilters {

    countries: PortsTerminalBerthsResponseItem[] = [];
    berths: PortsTerminalBerthsResponseItem[] = [];
    terminals: PortsTerminalBerthsResponseItem[] = [];
    ports: PortsTerminalBerthsResponseItem[] = [];
    selectedCountries: PortsTerminalBerthsResponseItem[] = [];
    selectedPorts: PortsTerminalBerthsResponseItem[] = [];
    selectedTerminals: PortsTerminalBerthsResponseItem[] = [];
    selectedBerths: PortsTerminalBerthsResponseItem[] = [];
    selectedFromDate: Date = null;
    selectedToDate: Date = null;

    minDate: Date;
    maxDate: Date;

    constructor(private portsAndTerminalsService: PortsAndTerminalsService, private notificationService: NotificationClientService) {
        this.loadPortsTerminalsAndBerths();
    }

    @bindable()
    public currentListView: IFilterableVesselPortList<any> = null;

    public filters: ParamFilterModel[] = [];

    lookupsLoaded: boolean = false;
    portTerminalAndBerthResponse: PortTerminalAndBerthResponse;

    applyAllFilters(reload: boolean = true) {
        const filters: ParamFilterModel[] = [];

        if (this.selectedFromDate && this.selectedToDate && this.selectedFromDate > this.selectedToDate) {
            this.notificationService.show('From date cannot be greater than to date !', '', NotificationType.Error, 5000);
        }

        if (this.selectedPorts) {
            filters.push({ parameterName: "portIds", dataType: DataType.Default, paramValue: PortsTerminalBerthsResponseItem.getIdsFromLookups(this.ports, this.selectedPorts).join(",") });
            filters.push({
                parameterName: "portNames", dataType: DataType.Default, paramValue: this.ports && this.selectedPorts &&
                    this.ports.length > 0 && this.selectedPorts.length > 0 ? this.selectedPorts.map(x => x.name).join(",") : null
            });
        }

        if (this.selectedCountries) {
            filters.push({ parameterName: "countryIds", dataType: DataType.Default, paramValue: PortsTerminalBerthsResponseItem.getIdsFromLookups(this.countries, this.selectedCountries).join(",") });
        }

        if (this.selectedTerminals) {
            filters.push({ parameterName: "terminalIds", dataType: DataType.Default, paramValue: PortsTerminalBerthsResponseItem.getIdsFromLookups(this.terminals, this.selectedTerminals).join(",") });
        }

        if (this.selectedBerths) {
            filters.push({ parameterName: "berthIds", dataType: DataType.Default, paramValue: PortsTerminalBerthsResponseItem.getIdsFromLookups(this.berths, this.selectedBerths).join(",") });
        }

        if (this.selectedFromDate) {
            filters.push({ parameterName: "etaFrom", dataType: DataType.DateTime, paramValue: this.selectedFromDate.toISOString() });
        }

        if (this.selectedToDate) {
            filters.push({ parameterName: "etaTo", dataType: DataType.DateTime, paramValue: this.selectedToDate.toISOString() });
        }

        this.filters = filters;

        //apply filters and reload
        if (reload)
            this.currentListView.applyFilters(this.filters);
    }

    getPorts($event) {
        if ($event && $event.detail && $event.detail.item) {
            const ports = this.portTerminalAndBerthResponse?.ports || [];
            this.ports = $event.detail.item.length == 0 && this.countries && this.countries.length > 0 ? ports : ports.filter(x => $event.detail.item.some(item => item.id === x.parentId));
            // if (this.ports.length == 1) {
            //     this.ports[0]["isSelected"] = true;
            //     this.selectedPorts = [this.ports[0]];
            // }
            this.getTerminals({ detail: { item: this.ports } });
        }
    }

    getTerminals($event) {
        if ($event && $event.detail && $event.detail.item) {
            const terminals = this.portTerminalAndBerthResponse?.terminals || [];
            this.terminals = $event.detail.item.length == 0 && this.ports && this.ports.length > 0 ? terminals : terminals.filter(x => $event.detail.item.some(item => item.id === x.parentId));
            // if (this.terminals.length == 1) {
            //     this.terminals[0]["isSelected"] = true;
            //     this.selectedTerminals = [this.terminals[0]];
            // }
            this.getBerths({ detail: { item: this.terminals } });
        }
    }

    getBerths($event) {
        if ($event && $event.detail && $event.detail.item) {
            const berths = this.portTerminalAndBerthResponse?.berths || [];
            this.berths = $event.detail.item.length == 0 && this.terminals && this.terminals.length > 0 ? berths : berths.filter(x => $event.detail.item.some(item => item.id === x.parentId));
            // if (this.berths.length == 1) {
            //     this.berths[0]["isSelected"] = true;
            //     this.selectedBerths = [this.berths[0]];
            // }
        }
    }

    loadPortsTerminalsAndBerths() {
        return new Promise<void>((resolve) => {
            this.portsAndTerminalsService.loadPortsTerminalsAndBerths().then(res => {
                this.portTerminalAndBerthResponse = res;
                this.countries = res.countries;
                this.berths = res.berths;
                this.ports = res.ports;
                this.terminals = res.terminals;
                this.lookupsLoaded = true;
                this.initializeFilters();
                this.applyAllFilters(false);
                if (!this.listViewChangeTriggered)
                    this.currentListViewChanged();
                resolve();
            }).catch(() => {
                this.notificationService.show("Error loading Countries, Ports, Terminals and Berths", "", NotificationType.Error)
                this.lookupsLoaded = true;
                if (!this.listViewChangeTriggered)
                    this.currentListViewChanged();
                resolve();
            });
        });
    }

    initializeFilters() {
        this.selectedCountries = [];
        this.selectedPorts = [];
        this.selectedTerminals = [];
        this.selectedBerths = [];
        if (this.currentListView.type == "pre-arrivals") {
            this.selectedFromDate = new Date(new Date().setHours(0, 0, 0, 0));
            this.selectedToDate = moment().add(7, 'd').hours(23).minutes(59).seconds(59).milliseconds(999).toDate();
        } else if (this.currentListView.type == "departures") {
            this.selectedFromDate = moment().add(-3, 'd').hours(0).minutes(0).seconds(0).milliseconds(0).toDate();
            this.selectedToDate = new Date();
        } else {
            this.selectedFromDate = null;
            this.selectedToDate = null;
        }
        //also reset filtered lists
        this.ports = this.portTerminalAndBerthResponse ? this.portTerminalAndBerthResponse.ports : [];
        this.terminals = this.portTerminalAndBerthResponse ? this.portTerminalAndBerthResponse.terminals : [];
        this.berths = this.portTerminalAndBerthResponse ? this.portTerminalAndBerthResponse.berths : [];
    }

    resetFiltersToDefault() {
        this.initializeFilters();
        this.applyAllFilters();
    }

    listViewChangeTriggered: boolean = false;
    currentListViewChanged() {
        if (this.currentListView.type == "pre-arrivals") {
            this.minDate = moment().add(-60, 'd').startOf('d').toDate();
            this.maxDate = moment().add(60, 'd').endOf('d').toDate();
        }
        else
        {
            this.minDate = undefined;
            this.maxDate = undefined;
        }

        if (this.lookupsLoaded && this.currentListView) {
            const model: PortRiskProfileFilterModel = {
                paramFilters: CloneHelper.cloneObject(this.filters),
                portTerminalAndBerthResponse: CloneHelper.cloneObject(this.portTerminalAndBerthResponse)
            }
            this.currentListView.updateFilterData(model);
            this.listViewChangeTriggered = true;
        }
        else {
            this.listViewChangeTriggered = false;
        }
    }

    public async reloadData() {
        this.listViewChangeTriggered = false;
        this.lookupsLoaded = false;
        this.initializeFilters();
        this.applyAllFilters(false);
        await this.loadPortsTerminalsAndBerths();
    }
}